import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

type SaveAction = 'save' | 'save-and-close';
const STORAGE_KEY = 'wpp-save-action';

interface SaveSplitButtonProps {
  onSave: () => Promise<void>;
  onSaveAndClose: () => Promise<void>;
  isPending: boolean;
  saveLabel?: string;
  savingLabel?: string;
}

export function SaveSplitButton({
  onSave,
  onSaveAndClose,
  isPending,
  saveLabel = 'Save Changes',
  savingLabel = 'Saving...',
}: SaveSplitButtonProps) {
  const [preferred, setPreferred] = useState<SaveAction>(
    () => (localStorage.getItem(STORAGE_KEY) as SaveAction) || 'save',
  );

  const persist = (action: SaveAction) => {
    localStorage.setItem(STORAGE_KEY, action);
    setPreferred(action);
  };

  const handlePrimary = () => {
    persist(preferred);
    if (preferred === 'save') onSave();
    else onSaveAndClose();
  };

  const handleSecondary = () => {
    const alt: SaveAction = preferred === 'save' ? 'save-and-close' : 'save';
    persist(alt);
    if (alt === 'save') onSave();
    else onSaveAndClose();
  };

  const primaryLabel = preferred === 'save' ? saveLabel : 'Save and Close';
  const secondaryLabel = preferred === 'save' ? 'Save and Close' : saveLabel;

  return (
    <div className="inline-flex items-stretch">
      <Button
        type="button"
        size="lg"
        className="rounded-r-none"
        disabled={isPending}
        onClick={handlePrimary}
      >
        {isPending ? savingLabel : primaryLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 inline-flex items-center justify-center w-9 rounded-r-md rounded-l-none border-l border-primary-foreground/30 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          disabled={isPending}
        >
          <ChevronDown className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleSecondary}>
            {secondaryLabel}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
